import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_CHECKOUT_BUTTON_SELECTOR = '.cart__ctas .cart__checkout-button';
const CART_CHECKOUT_MARKER = 'cartItemsUpdateWasEnabled';

/** Reference count so overlapping updates do not re-enable early. */
let cartCheckoutLoadingDepth = 0;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange.bind(this), 300);

  pendingUpdateEvent = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('submit', this.#handleFormSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('submit', this.#handleFormSubmit);
  }

  #handleFormSubmit = async (event) => {
    const form = event.target;
    if (form.id !== 'cart-form') return;

    event.preventDefault();
    await this.syncPendingCheckoutQuantities();

    try {
      const win = /** @type {any} */ (window);
      if (typeof win.syncCheckoutContextToCart === 'function') {
        await win.syncCheckoutContextToCart();
      }
    } catch (error) {
      console.error('Failed to sync checkout context before checkout:', error);
    } finally {
      window.location.href = '/checkout';
    }
  };

  async syncPendingCheckoutQuantities() {
    // Scan for any input where the user-typed value differs from the confirmed quantity in cart
    const inputs = this.querySelectorAll('input[data-cart-quantity]');
    let pendingInput = null;

    for (const input of inputs) {
      const currentValue = parseInt(input.value) || 0;
      const cartValue = parseInt(input.getAttribute('data-cart-quantity')) || 0;
      if (currentValue !== cartValue) {
        pendingInput = input;
        break;
      }
    }

    if (!pendingInput) return false;

    this.#debouncedOnChange.cancel();

    const quantity = parseInt(pendingInput.value) || 0;
    const line = Number(pendingInput.dataset.cartLine);
    const simulatedEvent = {
      target: pendingInput,
      detail: {
        quantity: quantity,
        cartLine: line,
      },
    };

    try {
      this.isCheckoutSyncing = true;
      await this.#onQuantityChange(simulatedEvent);
    } catch (error) {
      console.error('Failed to sync cart quantities before checkout:', error);
    } finally {
      this.isCheckoutSyncing = false;
    }

    return true;
  }

  /**
   * After the cart has been morphed to its new state, look for any warranty
   * row associated with the just-removed product and remove it. We track the
   * warranty by its stable line item key (data-key) captured before the first
   * fetch, then re-find it in the freshly morphed DOM to compute its current
   * 1-based line number.
   *
   * IMPORTANT: scope queries to `tbody` because the cart table also has a
   * visually-hidden `<thead>` row with the same `cart-items__table-row` class.
   * Including it would shift indices by 1 and send wrong line numbers to the
   * /cart/change.js endpoint, leaving the warranty in the cart.
   */
  #removeAssociatedWarrantyAfterMorph() {
    const warrantyKey = this.pendingWarrantyRemovalKey;
    this.pendingWarrantyRemovalKey = null;
    this.lastRemovedProductId = null;

    if (!warrantyKey) return;

    const warrantyRow = this.querySelector(`tbody .cart-items__table-row[data-key="${CSS.escape(warrantyKey)}"]`);
    if (!warrantyRow) return;

    const allRows = Array.from(this.querySelectorAll('tbody .cart-items__table-row'));
    const lineIndex = allRows.indexOf(warrantyRow);
    if (lineIndex === -1) return;

    this.onLineItemRemove(lineIndex + 1);
  }

  #getAssociatedWarrantyRow(itemRow) {
    if (!itemRow) return null;

    const parentKey = itemRow.dataset.key;
    if (!parentKey) return null;

    return (
      this.refs.cartItemRows.find((row) => {
        return row.dataset.parentKey === parentKey && row.classList.contains('warranty-product');
      }) || null
    );
  }


  #getDailyLimit() {
    const rawDailyLimit = window.productDailyLimitQty || this.dataset.dailyLimit || '0';
    const dailyLimit = parseInt(String(rawDailyLimit), 10);
    return Number.isNaN(dailyLimit) ? 0 : dailyLimit;
  }

  #showDailyLimitError(message, type = 'error') {
    // Use global toast function to show error
    if (window.showToast) {
      window.showToast(message + '.', type);
    } else {
      // Fallback to console if toast not available
      console.error('Daily limit exceeded:', message);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    // 1. Extract data from event
    let { quantity: requestedQuantity, cartLine: line } = event.detail;
    const selectorEl = event?.target?.closest?.('cart-quantity-selector-component, quantity-selector-component') || event?.target;
    const qtysoldAttr = selectorEl?.getAttribute('data-qty-sold');
    const qtysold = parseInt(qtysoldAttr, 10) || 0;

    if (!line) return;
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    // 2. Identify Product ID to track all related variants
    const productIdInput = lineItemRow.querySelector('[data-cart-line-product-id]');
    const currentProductId = productIdInput?.getAttribute('data-product-id') || productIdInput?.value;
    const dailyLimit = this.#getDailyLimit();

    // 3. Fetch current cart and calculate total quantity of OTHER variants
    let otherVariantsTotalQty = 0;
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Sum quantities for all items with same product_id, excluding current line
      const currentLineKey = lineItemRow.dataset.key;
      otherVariantsTotalQty = cart.items?.reduce((total, item) => {
        if (item.product_id?.toString() === currentProductId && item.key !== currentLineKey) {
          return total + item.quantity;
        }
        return total;
      }, 0) || 0;
    } catch (error) {
      console.error('Failed to fetch current cart quantity for product:', error);
      // Fallback to DOM calculation if fetch fails
      this.refs.cartItemRows.forEach((row, index) => {
        const rowProductIdInput = row.querySelector('[data-cart-line-product-id]');
        const rowProductId = rowProductIdInput?.getAttribute('data-product-id') || rowProductIdInput?.value;
        if (rowProductId === currentProductId && (index + 1) !== line) {
          const qtyInput = row.querySelector('input[data-cart-quantity]');
          otherVariantsTotalQty += parseInt(qtyInput?.value || qtyInput?.getAttribute('value') || '0', 10);
        }
      });
    }

    // 4. Calculate the maximum allowed quantity for this specific variant
    // Formula: Limit - Already Purchased - Other variants in current cart
    const maxAllowedForThisVariant = dailyLimit - qtysold - otherVariantsTotalQty;

    // 5. Validation and adjustment
    if (requestedQuantity > maxAllowedForThisVariant) {
      const displayLimit = Math.max(0, maxAllowedForThisVariant);

      this.#showDailyLimitError(
        `Total limit for this product is ${dailyLimit}. Based on your cart and history, you can only set this item to ${displayLimit}`,
        'warning'
      );

      // Cap the requested quantity to the allowed maximum
      requestedQuantity = displayLimit;

      // Stop update if the allowed quantity is 0 and user is trying to add more
      if (requestedQuantity <= 0 && event.detail.quantity > 0) {
        requestedQuantity = 0;
      }
    }

    // --- PROCEED WITH UPDATE LOGIC ---
    if (requestedQuantity === 0) {
      return this.onLineItemRemove(line);
    }

    const associatedWarrantyRow = this.#getAssociatedWarrantyRow(lineItemRow);

    if (associatedWarrantyRow) {
      const parentKey = lineItemRow.dataset.key;
      const warrantyKey = associatedWarrantyRow.dataset.key;

      if (parentKey && warrantyKey) {
        const updates = {
          [parentKey]: requestedQuantity,
          [warrantyKey]: requestedQuantity,
        };
        return this.#updateCartByUpdates(updates, 'change-multi');
      } else {
        // Fallback to single update
        return this.updateQuantity({
          line,
          quantity: requestedQuantity,
          action: 'change'
        });
      }
    } else {
      return this.updateQuantity({
        line,
        quantity: requestedQuantity,
        action: 'change'
      });
    }

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }
  lastRemovedProductId = null;
  pendingWarrantyRemovalKey = null;

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (cartItemRowToRemove && cartItemRowToRemove.classList.contains('warranty-product')) {
      const parentKey = cartItemRowToRemove.dataset.parentKey;
      if (parentKey) {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const parentItem = cart.items.find((item) => item.key === parentKey);

          if (parentItem) {
            const updatedProperties = { ...parentItem.properties };
            updatedProperties['Warranty'] = "Manufacturer's Warranty";

            await fetch(`${Theme.routes.cart_change_url}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                id: parentKey,
                quantity: parentItem.quantity,
                properties: updatedProperties,
              }),
            });
          }
        } catch (e) {
          console.error('Failed to update parent warranty property', e);
        }
      }
    }

    // Find associated warranty row (if any) so we can remove it in the same
    // round-trip and avoid the visible delay between the fridge being removed
    // and the cart truly emptying.
    const associatedWarrantyRow = this.#getAssociatedWarrantyRow(cartItemRowToRemove);
    let removedProductId = null;

    if (cartItemRowToRemove) {
      const productIdInput = cartItemRowToRemove.querySelector('[data-cart-line-product-id]');
      removedProductId = productIdInput?.value || null;
    }

    const parentKey = cartItemRowToRemove?.dataset?.key;
    const warrantyKey = associatedWarrantyRow?.dataset?.key;

    if (parentKey && warrantyKey) {
      // Atomic bulk removal — both line items go to 0 in a single
      // /cart/update.js call, so the empty state appears immediately after
      // one network round-trip instead of two sequential /cart/change.js calls.
      this.lastRemovedProductId = null;
      this.pendingWarrantyRemovalKey = null;
      return this.#updateCartByUpdates({ [parentKey]: 0, [warrantyKey]: 0 }, 'clear-multi');
    } else {
      // Single-line removal. If there's a warranty but it has no data-key
      // (very unlikely), fall back to the post-morph cleanup path.
      this.lastRemovedProductId = removedProductId;
      this.pendingWarrantyRemovalKey = warrantyKey || null;
      return this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];
    // Also animate the (CSS-hidden) warranty row when bulk-removing so the
    // refs stay consistent with the DOM after the morph swaps things in place.
    if (associatedWarrantyRow && !rowsToRemove.includes(associatedWarrantyRow)) {
      rowsToRemove.push(associatedWarrantyRow);
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Atomically update one or more cart line items by their stable `key`
   * (the `item.key` from Shopify, exposed as `data-key` on each row).
   * Uses /cart/update.js with the `updates` object so all keys are processed
   * server-side in one transaction and the response sections reflect the
   * final cart state — no follow-up fetch needed.
   *
   * @param {Object} updates - Object with cart line item keys as property names and quantities as values.
   * @param {string} markerName - Name for the performance marker.
   */
  async #updateCartByUpdates(updates, markerName = 'update-multi') {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${markerName}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const preferredStore = await this.#getPreferredStoreForCheckout();
    const attributes = {};
    if (preferredStore) {
      const storeCode = this.#getStoreCodeFromPreferredStore(preferredStore);
      if (storeCode) attributes['preferred_store_code'] = storeCode;
    }

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      ...(Object.keys(attributes).length > 0 && { attributes }),
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const errorMsg = parsedResponseText.errors;
          const inventoryMatch = typeof errorMsg === 'string' && errorMsg.match(/only\s+(\d+)/i);
          if (inventoryMatch) {
            this.#handleInventoryError(errorMsg);
            return;
          }

          console.error('cart/update.js errors:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: this.isCheckoutSyncing ? 'cart-checkout-sync' : 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, action } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Get preferred store and add store code to attributes
    const preferredStore = await this.#getPreferredStoreForCheckout();
    const attributes = {};
    if (preferredStore) {
      const storeCode = this.#getStoreCodeFromPreferredStore(preferredStore);
      if (storeCode) {
        attributes['preferred_store_code'] = storeCode;
      }
    }

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      ...(Object.keys(attributes).length > 0 && { attributes }),
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const errorMsg = parsedResponseText.errors;
          const inventoryMatch = typeof errorMsg === 'string' && errorMsg.match(/only\s+(\d+)/i);
          if (inventoryMatch) {
            this.#handleInventoryError(errorMsg);
            return;
          }

          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: this.isCheckoutSyncing ? 'cart-checkout-sync' : 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();

        if (action === 'clear') {
          this.#removeAssociatedWarrantyAfterMorph();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles inventory limitation errors by displaying a toast warning
   * and refreshing the cart section to sync quantities.
   * @param {string} errorMsg - The error message.
   */
  #handleInventoryError(errorMsg) {
    if (window.showToast) {
      window.showToast(errorMsg, 'warning');
    } else {
      console.warn(errorMsg);
    }
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.#setCheckoutButtonsSyncDisabled(true);
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.#setCheckoutButtonsSyncDisabled(false);
  }

  /**
   * Set checkout buttons disabled state during cart updates.
   * @param {boolean} disabled
   */
  #setCheckoutButtonsSyncDisabled(disabled) {
    const checkoutButtons = document.querySelectorAll(CART_CHECKOUT_BUTTON_SELECTOR);
    checkoutButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (disabled) {
        // Preserve original disabled state (e.g., empty cart).
        if (!button.disabled) {
          button.dataset[CART_CHECKOUT_MARKER] = 'true';
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        return;
      }

      if (button.dataset[CART_CHECKOUT_MARKER] === 'true') {
        button.disabled = false;
        delete button.dataset[CART_CHECKOUT_MARKER];
      }
      button.removeAttribute('aria-busy');
    });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Get store code from preferred store for checkout attributes.
   * @param {{ storeCode?: string, _apiData?: { storeCode?: { value?: string } } } | null} store - Preferred store object
   * @returns {string} Store code or empty
   */
  #getStoreCodeFromPreferredStore(store) {
    if (!store) return '';
    const code = store._apiData?.storeCode?.value;
    return code != null && code !== '' ? String(code).trim() : '';
  }

  /**
   * Get preferred store for checkout attributes
   * Uses global utility function or component instance to avoid code duplication
   */
  async #getPreferredStoreForCheckout() {
    try {
      // Try global utility function first (most efficient)
      const win = /** @type {any} */ (window);
      if (typeof win !== 'undefined' && typeof win.getPreferredStore === 'function') {
        return await win.getPreferredStore();
      }

      // Fallback: try to get from component instance
      const component = /** @type {any} */ (document.querySelector('store-locator-component'));
      if (component && typeof component.loadSelectedStore === 'function') {
        return await component.loadSelectedStore();
      }

      return null;
    } catch (e) {
      console.error('Error getting preferred store for checkout:', e);
      return null;
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
